/**
 *  @file       eth.js
 *              Library code for manipulating ethereum keys, addresses, etc
 *              with syntactic sugar and abstractions for common but error-prone
 *              operations like equivalence comparison.
 *
 *  @module     eth
 *  @author     Wes Garland, wes@kingds.network
 *  @date       July 2019
 */
'use strict';
const debugging = require('dcp/debugging').scope('wallet/eth');
const ethUtil = require('./make-signature')._internalEth.util;
const Buffer = require('./make-signature')._internalEth.buffer.Buffer;
const { makeSignedMessage } = require('./make-signature');
const { DCPError } = require('dcp/common/dcp-error');

if (!ethUtil)
  throw new Error('eth module was loaded before ethereum internals were initialized in keystore module');

/**
 * Static function which 'coerces' to Address, constructing a new instance of Address only as needed.
 * @see exports.Address()
 */
/**
 *  Class which represents an Ethereum address (public key); used in DCP for public
 *  mentions of things like proof of user identity, bank account numbers, etc.
 *
 * @class Address
 * @memberof module:dcp/wallet
 *  @param address      {string|object} The address we want to represent. If the
 *                                      passed address is a string, it should be
 *                                      (but is not required to be) in checksum format;
 *                                      a leading 0x is not required.  If the address
 *                                      is an object, we coerce the object with
 *                                      toString(16) before working with it (BigNumber
 *                                      and similar objects are expected to "just work")
 *
 * *note* -     An address which arrives as an all-uppercase string and no leading 0x
 *              will not have its checksum validated.  This is to ease debugging against
 *              MySQL database copy-and-paste and should have a negligible real-world
 *              impact.
 * @param       {boolean} [testOnly]  when true, do not throw an error if the address fails to construct,
 *                                    instead return false. Return true if the address can correctly be constructed
 *
 * @throws      {TypeError}       when the address is invalid, fails checksum, etc.
 * @throws      {TypeError}       when the address is a number; eg a programmer typed in
 *                              a hexadecimal literal.
 * @returns     {module:dcp/wallet.Address|boolean} instance of Address whose value is a checksummed string of hex
 *              digits with leading 0x or a boolean corresponding to the result of the `testOnly` flag.
 * @access public
 */
exports.Address = function dcpEth$$Address(address, testOnly) {
  let cksumAddress

  if (!(this instanceof dcpEth$$Address))
  {
    /* static function, not constructor */
    if (arguments.length === 1)
    {
      /* cast */
      if (address instanceof dcpEth$$Address)
        return address;
      else
        return new dcpEth$$Address(address);
    }
  }
  else
  {
    if (testOnly && debugging())
      console.warn('superflous address object detected at', (new Error().stack || '').split('\n').slice(2)[1]);
  }

  if (typeof address === 'undefined' || address === null) {
    if (testOnly)
      return false
    throw new TypeError(`Invalid address (${typeof address === 'string' ? address : typeof address})`);
  }

  if (typeof address === 'number') {
    if (testOnly)
      return false
    throw new TypeError('There is not enough resolution in a Number to store an Ethereum address')
  }

  if (typeof address !== 'string') {
    if (Buffer.isBuffer(address))
      address = address.toString('hex')
    else
      address = address.toString(16)
  }

  if (address.match(/^0x/))
    address = address.slice(2)

  if (!address.match(/^[a-fA-F0-9]*$/)) {
    if (testOnly)
      return false
    throw new TypeError(`Address must be composed of only hex digits; ${address} is invalid`)
  }

  if (address.length !== 40) {
    if (testOnly)
      return false
    throw new TypeError(`Addresses must be 40 digits long; ${address} is ${address.length} digits`)
  }

  address = '0x' + address.padStart(40, 0)

  if (address.toLowerCase() === address) {
    cksumAddress = ethUtil.toChecksumAddress(address)
  } else {
    // Prevent 0X prefix from always failing the uppercase check.
    if (address === `0x${(address.slice(2)).toUpperCase()}`) {
      cksumAddress = ethUtil.toChecksumAddress(address)
    } else {
      if (address !== ethUtil.toChecksumAddress(address)) {
        if (testOnly)
          return false
        throw new TypeError(`Bad checksum for address ${address}`)
      }
      cksumAddress = address
    }
    address = address.toLowerCase()
  }

  if (testOnly)
    return true;

  if (this.constructor !== exports.Address)
    throw new Error("Address constructor called without 'new'");

  Object.defineProperty(this, 'valueOf', {
    value: function dcpEth$$Address$valueOf() {
      return cksumAddress
    },
    configurable: false,
    writable: false,
    enumerable: false
  })
}
exports.nullAddress = new exports.Address('0000000000000000000000000000000000000000'); /* no possible private key */

exports.isAddress = function dcpEth$$isAddress(address) {
  return exports.Address(address, true)
}

/**
 *  Equality comparsion.
 *  @method module:dcp/wallet.Address#eq
 *  @param {string|object} address2 The address we want to compare.
 *          May be used as an argument to the Address() constructor.
 *  @returns {boolean} true if address2 represents the same eth address as this instance; false otherwise.
 *  @access public
 */
exports.Address.prototype.eq = function dcpEth$$Address$eq(address2) {
  if (!(address2 instanceof exports.Address))
  {
    if (exports.isAddress(address2))
      address2 = new exports.Address(address2);
    else
      return false;
  }
  return this.valueOf() === address2.valueOf();
}

/**
 *  @method module:dcp/wallet.Address#toJSON
 *  @returns {string} An Ethereum address in checksum format, with a leading 0x.
 *  @access public
 *//**
 *  @method module:dcp/wallet.Address#toString
 *  @returns {string} An Ethereum address in checksum format, with a leading 0x.
 *  @access public
 *//**
 * The toJSON alias is to ensure that Address instances will be coerced
 *  to a string by JSON.stringify.
 */
exports.Address.prototype.toJSON =
exports.Address.prototype.toString = function dcpEth$$Address$toString() {
  return this.valueOf()
}

/**
 * Corresponds-To method. Determines if this Address corresponds to the passed private key.
 *  @see {@link module:dcp/wallet.PrivateKey#ct|PrivateKey#ct}
 *  @param   {string|object}   privateKey      An instance of PrivateKey or a value suitable for
 *                              the PrivateKey constructor
 *  @returns {boolean}   true when this address corresponds to the passed privateKey
 *  @access public
 *  @method module:dcp/wallet.Address#ct
 */
exports.Address.prototype.ct = function dcpEth$$Address$ct(privateKey) {
  if (!exports.isPrivateKey(privateKey)) {
    return false;
  }

  return this.eq(new (exports.PrivateKey)(privateKey).toAddress())
}

/** Verifies that an ethereum signature for the message came from this address.
 * @method module:dcp/wallet.Address#verifySignature
 *  @param      {any} messageBody   The message that the signature was created from.
 *                It will be stringified via JSON.stringify.
 *  @param      {object|string} signature  The signature to compare against the address and
 *                messageBody. If it is a string, it will be coerced to an object via JSON.parse.
 *  @returns    {boolean} true when this address matches the address that signed the message
 *  @access public
 */
exports.Address.prototype.verifySignature = function dcpEth$$Address$verifySignature(messageBody, signature)
{
  var msgBuf = Buffer.from(JSON.stringify(messageBody));
  var publicKey;

  if (typeof signature === 'string')
    signature = JSON.parse(signature);

  publicKey = ethUtil.ecrecover(ethUtil.hashPersonalMessage(msgBuf),
                                signature.v,
                                Buffer.from(signature.r),
                                Buffer.from(signature.s));

  return this.eq(ethUtil.pubToAddress(publicKey));
}

exports.recoverAddressfromSignature = function dcpEth$$recoverAddressFromSignature(messageBody, signature)
{
  if (typeof messageBody === 'object')
    messageBody = JSON.stringify(messageBody);
  if (typeof signature === 'string')
    signature = JSON.parse(signature);

  const msgBuf = Buffer.from(messageBody);
  const publicKey = ethUtil.ecrecover(ethUtil.hashPersonalMessage(msgBuf),
                                      signature.v,
                                      Buffer.from(signature.r),
                                      Buffer.from(signature.s));

  return ethUtil.pubToAddress(publicKey);
}

/**
 * Recover an address from a signed message
 *
 * @param {object|string} message           ethereum signed message
 * @param {Buffer}        messageBodyHash   [optional] - ethereum personal message hash of message body
 * @returns {Address}
 * @throws  if message not signed by message.owner
 */
exports.Address.fromMessage = function dcpEth$$Address$fromMessage(message, messageBodyHash)
{
  var address;

  if (typeof message === 'string' || message instanceof String)
    message = JSON.parse(message);

  if (!messageBodyHash)
  {
    const msgBuf = Buffer.from(JSON.stringify(message.body));
    messageBodyHash = ethUtil.hashPersonalMessage(msgBuf);
  }

  const publicKey = ethUtil.ecrecover(messageBodyHash, message.signature.v,
                                      Buffer.from(message.signature.r),
                                      Buffer.from(message.signature.s));

  if (exports.Address.fromPublicKey) /* future use - fast crypto branch /wg feb 2026 */
    address = exports.Address.fromPublicKey(publicKey);
  else
    address = new exports.Address(ethUtil.pubToAddress(publicKey));

  if (!address.eq(message.owner))
    throw new Error('owner mismatch');
  return address;
}

/**
 * @method module:dcp/wallet.Address#toBuffer
 * @returns {Buffer} buffer of the address.
 * @access public
 */
exports.Address.prototype.toBuffer = function dcpEth$$Address$toBuffer() {
  const addressString = this.valueOf().slice(2);
  return Buffer.from(addressString, 'hex');
}

/**
 * Patches an object graph, transforming Address like values into instances of
 * exports.Address. Cyclic graphs are supported; depth limited to stack size.
 * Edges are composed of own properties.
 *
 * @param {Object} object The object graph to patch
 * @param {Array<Object>} [seen=[]] A list of objects that have been traversed
 */
exports.Address.patchUp = async function patchUp(object, seen = []) {
  seen.push(object);
  for (const key in object) {
    // Check for own properties
    if (Object.hasOwnProperty.call(object, key)) {
      const value = object[key];
      if (
        (typeof value !== 'object' && typeof value !== 'string') ||
        value === null ||
        seen.indexOf(value) !== -1
      ) {
        return;
      }

      if (exports.isAddress(value)) {
        object[key] = new exports.Address(value);
        // The Address is wrapped in a Promise (e.g. identities in dcpConfig)
      } else if (exports.isAddress(await value)) {
        object[key] = Promise.resolve(new exports.Address(await value));
      } else {
        // Continue traversing
        exports.Address.patchUp(value, seen);
      }
    }
  }
};

/**
 *  Class which represents an Ethereum private key used in DCP for things
 *  like proof of user identity, bank account ownership, etc.
 *
 *  @class PrivateKey
 *  @memberof module:dcp/wallet
 *  @param {string|object} privateKey   The private key we want to represent. If the
 *                                      passed private key is a string, it should (but
 *                                      is not required to) have the 0x prefix. If
 *                                      privateKey is an object, we coerce the object with
 *                                      toString(16) before working with it (BigNumber
 *                                      and similar objects are expected to "just work").
 * @param  {boolean}     testOnly       when true, return true only when the privateKey argument could
 *                              be used to construct without throwing
 * @throws      TypeError       when privateKey is invalid
 * @throws      TypeError       when privateKey is a number; eg a programmer typed in
 *                              a hexadecimal literal.
 * @returns     instance of PrivateKey whose value is a checksummed string of hex
 *              digits with leading 0x.
 * @access public
 * @link        https://distributive.atlassian.net/browse/DCP-4423
 */
exports.PrivateKey = function dcpEth$$PrivateKey(privateKey, testOnly) {
  if (typeof privateKey === 'undefined' || privateKey === null) {
    if (testOnly)
      return false
    throw new TypeError('Invalid private key')
  }

  if (typeof privateKey === 'number') {
    if (testOnly)
      return false
    throw new TypeError('There is not enough resolution in a Number to store an Ethereum private key')
  }

  if (typeof privateKey !== 'string') {
    if (Buffer.isBuffer(privateKey))
      privateKey = privateKey.toString('hex')
    else
      privateKey = privateKey.toString(16)
  }

  if (privateKey.match(/^0x/))
    privateKey = privateKey.slice(2)

  if (!privateKey.match(/^[a-fA-F0-9]*$/)) {
    if (testOnly)
      return false
    throw new TypeError(`Private key must be composed of only hex digits; ${privateKey} is invalid`)
  }

  if (privateKey.length !== 64) {
    if (testOnly)
      return false;
    throw new TypeError(`Private key ${privateKey} is too short`);
  }

  if (testOnly)
    return true

  privateKey = '0x' + privateKey.padStart(64, 0).toLowerCase()
  try {
    Object.defineProperty(this, 'valueOf', {
      value: function dcpEth$$PrivateKey$valueOf() {
        return privateKey
      },
      configurable: false,
      writable: false,
      enumerable: false
    }) } catch(e) {
    throw new Error('PrivateKey constructor must be invoked with new operator')
  }
}

exports.PrivateKey.prototype.unlock = function dcpEth$$PrivateKey$unlock() {
  // have this function to match with keystore class
}

exports.PrivateKey.prototype.lock = function dcpEth$$PrivateKey$lock() {
  throw new DCPError('PrivateKey cannot be locked!')
}

exports.PrivateKey.prototype.makeSignedMessage = function dcpEth$$PrivateKey$makeSignedMessage(messageBody) {
  return makeSignedMessage(messageBody, this);
}

/**
 * Creates and returns signature for messageBody
 *       { owner: this.address, signature: signature, auth: messageLightWeight, body: messageBody };
 * The signature is computed only on messageLightWeight, in order to improve perf when messageBody is large.
 * @async
 * @access public
 * @param {object} messageBody
 * @returns {Promise<Signature>}
 * @function module:dcp/wallet.PrivateKey#makeSignature
 *
 * @note   This function's documentation is almost a verbatim copy of the Keystore method of the same
 *         name, as they are effectively the same function.
 */
exports.PrivateKey.prototype.makeSignature = async function wallet$$PrivateKey$makeSignature(messageBody)
{
  return require('./keystore').Keystore.prototype.makeSignature.call(this, messageBody);
}

/**
 * Creates and returns signature for messageBody
 *       { owner: this.address, signature: signature, auth: messageLightWeight, body: messageBody };
 * The signature is computed only on messageLightWeight, in order to improve perf when messageBody is large.
 * @async
 * @access public
 * @param {object} messageBody
 * @returns {Promise<Signature>}
 * @function module:dcp/wallet.PrivateKey#makeSignature
 *
 * @note   This function's documentation is almost a verbatim copy of the Keystore method of the same
 *         name, as they are effectively the same function.
 */
exports.PrivateKey.prototype.makeSignature = async function wallet$$PrivateKey$makeSignature(messageBody)
{
  return require('./keystore').Keystore.prototype.makeSignature.call(this, messageBody);
}

exports.PrivateKey.prototype.getPrivateKey = function PrivateKey$getPrivateKey() {
  return this;
}

exports.isPrivateKey = function dcpEth$$isPrivateKey(pk) {
  return exports.PrivateKey(pk, true)
}

/**
 *  Equality comparsion.
 *  @method module:dcp/wallet.PrivateKey#eq
 *  @param {string|object} privateKey2 The private key we want to compare. May be
 *                                     used as an argument to the PrivateKey()
 *                                     constructor.
 *  @returns  {boolean}  true if privateKey2 represents the same eth private key as this instance;
 *              false otherwise.
 * @access public
 */
exports.PrivateKey.prototype.eq = function dcpEth$$PrivateKey$eq(privateKey2) {
  if (!exports.isPrivateKey(privateKey2)) {
    return false;
  }

  let privateKey = this.valueOf()
  if (this === privateKey2 || privateKey === privateKey2)
    return true
  return privateKey.toString() === new (exports.PrivateKey)(privateKey2).toString()
}

/**
 * Corresponds-To method. Determines if this PrivateKey corresponds to the passed address.
 *  @see {@link module:dcp/wallet.Address#ct|Address#ct}
 *  @method module:dcp/wallet.PrivateKey#ct
 *  @param  {string|object} address   An instance of Address or a value suitable for the
 *                     Address constructor
 *  @returns {boolean} true when this address corresponds to the passed privateKey
 *  @access public
 */
exports.PrivateKey.prototype.ct = function dcpEth$$PrivateKey$ct(address) {
  return this.toAddress().eq(address)
}

/**
 *  @method module:dcp/wallet.PrivateKey#toString
 *  @returns An Ethereum private key including leading 0x.
 *  @access public
 */
exports.PrivateKey.prototype.toString = function dcpEth$$PrivateKey$toString() {
  return this.valueOf()
}

exports.PrivateKey.prototype.toBuffer = exports.Address.prototype.toBuffer;

/**
 * Determine the address (public key) associated with this instance of PrivateKey.
 * Only performs the hash once, no matter how many times you call the method.
 * *note* - This has a side effect of converting the method call from an inherited
 *          property to an own property on first call.
 * @method module:dcp/wallet.PrivateKey#toAddress
 * @returns {module:dcp/wallet.Address}
 * @access public
 */
exports.PrivateKey.prototype.toAddress = function PrivateKey() {
  let address = new (exports.Address)(ethUtil.privateToAddress(this.toBuffer()))
  Object.defineProperty(this, 'toAddress', {
    value: function dcpEth$$PrivateKey$toAddress() {
      return address
    },
    configurable: false,
    writable: false,
    enumerable: false
  })
  return this.toAddress()
}

Object.defineProperty(exports.PrivateKey.prototype, 'address', {
  get: function PrivateKey$$address_getter() {
    return this.toAddress();
  }
});

const inspect = Symbol.for('nodejs.util.inspect.custom');
exports.PrivateKey.prototype[inspect] = function () { return '[Object dcpEth$$PrivateKey(' + this.toString() + ')]' }
exports.Address.prototype[inspect] = function () { return '[Object dcpEth$$Address(' + this.toString() + ')]' }
